import type { ReportAsOf, ReportBlock, ReportColumn, ReportRow } from './cashPositionReport'
import type { SectionTone } from './cashPositionRows'

/**
 * Look of the downloadable Cash Position Summary, shared by the PDF and XLSX
 * writers so both files style the same report the same way. The palette
 * follows the customer's printed sample. Colours are CSS hex strings
 * ('#FFC000'), which jsPDF and autotable take as they are; the XLSX writer
 * turns them into exceljs ARGB. Font sizes stay with the writers — only the
 * "one step larger" `emphasis` flag is shared.
 */

const BLACK = '#000000'
const WHITE = '#FFFFFF'
const DARK_GREY = '#595959'
const LIGHT_GREY = '#D9D9D9'
const BLUE = '#0070C0'

const HEAD_FILL: Record<ReportColumn['kind'], string> = {
  combined: '#002060',
  pack: BLUE,
  facility: BLACK,
}
/**
 * The file follows the printed sample, not the screen: the `indigo` tone (the
 * balances block) is yellow and the `blue` tone (the LINE OF CREDIT block) is
 * mint here on purpose, as the sample's bands are.
 */
const SECTION_FILL: Record<SectionTone, string> = {
  indigo: '#FFFF00',
  blue: '#99FFCC',
  amber: '#FFC000',
}
const CAPS_FILL = '#B4C6E7'
const EOW_FILL = '#CCECFF'
const KEY_BALANCE_FILL = '#DDEBF7'
const VARIANCE_FILL = '#FFFFCC'
const DETAIL_FILL = '#FFE1CD'
const DETAIL_TEXT = DARK_GREY

export interface ReportCellStyle {
  /** Background, or null for none. */
  fill: string | null
  textColor: string
  bold: boolean
  italic: boolean
  /** One font size step larger: section headings and caps rows. */
  emphasis: boolean
  /** Upper-case the text (caps row labels, the combined column header). */
  upper: boolean
}

export interface ReportHeaderStyle extends ReportCellStyle {
  align: 'left' | 'center' | 'right'
}

/** Style of one report row: its label cell and its value cells. */
export interface ReportRowStyle {
  label: ReportCellStyle
  /** Value cells in facility columns. */
  values: ReportCellStyle
  /**
   * Value cells in pack and combined columns. They are subtotals, so they read
   * bold, and black even on a grey detail line, as the printed sample has them.
   */
  totals: ReportCellStyle
}

const PLAIN: ReportCellStyle = {
  fill: null,
  textColor: BLACK,
  bold: false,
  italic: false,
  emphasis: false,
  upper: false,
}

/** One box of the report's top line. */
export interface ReportTopLineCell {
  text: string
  style: ReportCellStyle
}

/**
 * The report's top line, left to right: the "Most Recently Recorded Date:"
 * caption, the as-of date and its weekday in parentheses, each in its own box.
 */
export function reportTopLine(asOf: ReportAsOf): [ReportTopLineCell, ReportTopLineCell, ReportTopLineCell] {
  return [
    { text: 'Most Recently Recorded Date:', style: { ...PLAIN, fill: DARK_GREY, textColor: WHITE, bold: true } },
    { text: asOf.date, style: { ...PLAIN, fill: '#BF8F00', textColor: WHITE, bold: true } },
    { text: `(${asOf.weekday})`, style: { ...PLAIN, fill: LIGHT_GREY, bold: true } },
  ]
}

/**
 * Header cell of a data column — combined navy, packs blue, facilities black,
 * white bold text — or of the label column when `column` is null.
 */
export function reportHeaderStyle(column: ReportColumn | null): ReportHeaderStyle {
  const base = { ...PLAIN, textColor: WHITE, bold: true }
  if (column === null) return { ...base, fill: HEAD_FILL.facility, align: 'left' }
  return {
    ...base,
    fill: HEAD_FILL[column.kind],
    upper: column.kind === 'combined',
    align: column.kind === 'facility' ? 'right' : 'center',
  }
}

/**
 * Style of every row of a block, index-aligned with `block.rows`. Precedence,
 * first match wins: section band, caps (BOOK BALANCE, CASH + CREDIT, the
 * remainder's ending balance), end-of-week (the week blocks' ending balances),
 * projected subtotal (plain — the pack and combined columns still read bold
 * through {@link reportValueCellStyle}), net cash flow (grey band, bold), key
 * balance (pale blue band, bold), variance (pale yellow band, bold italic), any
 * other total (bold), projection detail line (grey italic on peach), else
 * plain. A section row fills only its label cell; its value cells carry the
 * blue {@link reportSectionCaption}.
 */
export function reportBlockRowStyles(block: ReportBlock): ReportRowStyle[] {
  return block.rows.map(rowStyle)
}

/**
 * Text of a section heading row's cell in `column`: "Total" above the combined
 * and pack columns, as the printed sample has it, or null for a facility.
 */
export function reportSectionCaption(column: ReportColumn): string | null {
  return column.kind === 'facility' ? null : 'Total'
}

/** A value cell of `row` in `column`. */
export function reportValueCellStyle(row: ReportRowStyle, column: ReportColumn): ReportCellStyle {
  return column.kind === 'facility' ? row.values : row.totals
}

function rowStyle(row: ReportRow): ReportRowStyle {
  if (row.kind === 'section') {
    const values: ReportCellStyle = { ...PLAIN, bold: true, textColor: BLUE }
    return { label: { ...PLAIN, bold: true, emphasis: true, fill: SECTION_FILL[row.tone] }, values, totals: values }
  }
  if (row.caps) {
    const values: ReportCellStyle = { ...PLAIN, bold: true, emphasis: true, fill: CAPS_FILL }
    return { label: { ...values, upper: true }, values, totals: values }
  }
  if (row.eow) return same({ ...PLAIN, bold: true, fill: EOW_FILL })
  if (row.subtotal) return same(PLAIN)
  if (row.netCashFlow) return same({ ...PLAIN, bold: true, fill: LIGHT_GREY })
  if (row.keyBalance) return same({ ...PLAIN, bold: true, fill: KEY_BALANCE_FILL })
  if (row.variance) return same({ ...PLAIN, bold: true, italic: true, fill: VARIANCE_FILL })
  if (row.kind === 'total') return same({ ...PLAIN, bold: true })
  if (row.projectionDetail) {
    const detail = same({ ...PLAIN, italic: true, textColor: DETAIL_TEXT, fill: DETAIL_FILL })
    return { ...detail, totals: { ...detail.totals, textColor: BLACK } }
  }
  return same(PLAIN)
}

function same(style: ReportCellStyle): ReportRowStyle {
  return { label: style, values: style, totals: { ...style, bold: true } }
}
